import React, { useState, useEffect } from "react";
import { IconButton, TextField, Snackbar, Button, Typography, Box, Tooltip, Alert, Chip, Card, CardContent, List, ListItem, ListItemText, ListItemIcon, Divider, Paper } from "@mui/material";
import DeleteBlastDialog from "./DeleteBlastDialog";
import SendBlastDialog from "./SendBlastDialog";
import DeliveryStatsDialog from "./DeliveryStatsDialog";
import ScheduleBlastDialog from "./ScheduleBlastDialog";
import ScheduledDateDisplay from "./ScheduledDateDisplay";
import { useAccount } from "../../contexts/AccountContext";
import { getUsers } from "../../services/userService";
import prohibitedWords from "../../constants/prohibitedWords";
import Confetti from "react-confetti";
import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
    TableCell,
} from "../ui/table";
import { Input } from "../ui/input";
import { Trash2, Send, PlusIcon, Calendar, Sparkles } from "lucide-react";
import './BlastsCSS/BlastTable.css';
import { api } from '../../utils/api';
import TargetingDemographicsDialog from './TargetingDemographicsDialog';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { CircularProgress } from '@mui/material';

export default function BlastTable() {
    const [blasts, setBlasts] = useState([]);
    const [addedBlast, setAddedBlast] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editMessage, setEditMessage] = useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [blastToDelete, setBlastToDelete] = useState(null);
    const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
    const [blastToSend, setBlastToSend] = useState(null);
    const [userCount, setUserCount] = useState(0);
    const { currentAccountId, userData } = useAccount();
    const [deliveryStatsOpen, setDeliveryStatsOpen] = useState(false);
    const [deliveryStats, setDeliveryStats] = useState({
        totalAttempted: 0,
        successfulSends: 0,
        failedSends: [],
    });
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showConfetti, setShowConfetti] = useState(false);
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [blastToSchedule, setBlastToSchedule] = useState(null);
    const [targetingDialogOpen, setTargetingDialogOpen] = useState(false);
    const [selectedDemographics, setSelectedDemographics] = useState({
        selectedGender: 'all',
        selectedAgeRange: 'all',
        includeMembership: false
    });
    const [recipientCounts, setRecipientCounts] = useState({});
    const [recommendations, setRecommendations] = useState([]);
    const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
    const [contextInput, setContextInput] = useState('');
    const [loadingIdeas, setLoadingIdeas] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            const token = localStorage.getItem('authToken');
            if (currentAccountId && token) {
                try {
                    await loadBlasts(currentAccountId);
                    await loadUserCount(currentAccountId);
                } catch (error) {
                    console.error('Error loading initial data:', error);
                    setErrorMessage(error.message || 'Failed to load data');
                    setErrorSnackbarOpen(true);
                }
            } else if (currentAccountId && !token) {
            }
        };

        loadData();
    }, [currentAccountId]);

    useEffect(() => {
    }, [blasts]);

    const loadUserCount = async (accountId) => {
        const users = await getUsers(accountId);
        setUserCount(users.length);
    };

    const loadBlasts = async (accountId) => {
        try {
            if (!accountId) {
                console.error('No accountId provided to loadBlasts');
                setErrorMessage('No account ID available');
                setErrorSnackbarOpen(true);
                return;
            }

            const response = await api.get(`/api/blasts/${accountId}`);


            if (Array.isArray(response)) {
                setBlasts(response);
            } else if (response && typeof response === 'object') {
                setBlasts(Array.isArray(response.data) ? response.data : []);
            } else {
                console.error('Invalid response format:', response);
                setErrorMessage('Invalid data format received from server');
                setErrorSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Error fetching blasts:', error);
            setErrorMessage(error.message || 'Failed to load blasts');
            setErrorSnackbarOpen(true);
        }
    };

    const handleDeleteClick = (blast) => {
        setBlastToDelete(blast);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await api.delete(`/api/blasts/${blastToDelete.id}?accountId=${currentAccountId}`);
            setBlastToDelete(null);
            setDeleteConfirmOpen(false);
            loadBlasts(currentAccountId);
        } catch (error) {
            console.error('Error deleting blast:', error);
            setErrorMessage(error.message);
            setErrorSnackbarOpen(true);
        }
    };

    const handleEditSubmit = async (id) => {
        if (!editMessage.trim()) {
            setErrorMessage('Please enter a message for the blast');
            setErrorSnackbarOpen(true);
            return;
        }
        try {
            await api.put(`/api/blasts/${id}`, {
                message: editMessage,
                updatedAt: new Date().toISOString(),
                accountId: currentAccountId
            });
            setEditingId(null);
            setEditMessage('');
            loadBlasts(currentAccountId);
        } catch (error) {
            console.error('Error updating blast:', error);
            setErrorMessage(error.message);
            setErrorSnackbarOpen(true);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!addedBlast.trim()) {
            setErrorMessage('Please enter a message for the blast');
            setErrorSnackbarOpen(true);
            return;
        }
        try {
            await api.post('/api/blasts', {
                message: addedBlast,
                status: 'draft',
                accountId: currentAccountId
            });
            setAddedBlast('');
            loadBlasts(currentAccountId);
        } catch (error) {
            console.error('Error creating blast:', error);
            setErrorMessage(error.message);
            setErrorSnackbarOpen(true);
        }
    };

    const handleSendClick = (blast) => {
        if (blast) {
            // Check prohibited words FIRST
            const foundProhibitedWord = prohibitedWords.find((word) =>
                blast.message.toLowerCase().includes(word)
            );
            if (foundProhibitedWord) {
                setErrorMessage(
                    `The message contains a prohibited word: "${foundProhibitedWord}"`
                );
                setErrorSnackbarOpen(true);
                return;
            }
            // If no prohibited words, set blast and open targeting dialog
            setBlastToSend(blast);
            setTargetingDialogOpen(true);
        }
    };

    const handleScheduleClick = (blast) => {
        setBlastToSchedule(blast);
        setScheduleDialogOpen(true);
    };

    const handleScheduleConfirm = async (selectedDate, targetingOptions) => {
        if (!selectedDate) {
            setErrorMessage('Please select a date to schedule the blast');
            setErrorSnackbarOpen(true);
            return;
        }
        try {
            await api.post(`/api/schedule/schedule/${currentAccountId}/${blastToSchedule.id}`, {
                scheduledDate: selectedDate,
                targeting: targetingOptions || { selectedGenders: ['all'], selectedAgeRange: 'all', membershipStatus: 'all' }
            });
            setBlastToSchedule(null);
            setScheduleDialogOpen(false);
            loadBlasts(currentAccountId);
        } catch (error) {
            setErrorMessage(error.message);
            setErrorSnackbarOpen(true);
        }
    };

    const handleTargetingConfirm = async (demographics) => {
        setSelectedDemographics(demographics);


        try {
            // Get the actual filtered count by making a test request
            const testResult = await api.post(`/api/blasts/test-count`, {
                accountId: currentAccountId,
                filters: {
                    selectedGenders: demographics.selectedGenders.includes('all') ? null : demographics.selectedGenders,
                    ageRange: demographics.selectedAgeRange === 'all' ? null : demographics.selectedAgeRange,
                    membershipStatus: demographics.membershipStatus === 'all' ? null : demographics.membershipStatus
                }
            });

            // Update the blast with the actual filtered count
            if (blastToSend) {
                setBlastToSend({
                    ...blastToSend,
                    filteredUserCount: testResult.matchedUsers || 0
                });
            }

            setTargetingDialogOpen(false);
            setSendConfirmOpen(true);
        } catch (error) {
            setErrorMessage('Failed to get filtered user count');
            setErrorSnackbarOpen(true);
        }
    };

    const handleSendConfirm = async () => {
        if (blastToSend) {
            try {
                const result = await api.post(`/api/blasts/${blastToSend.id}/send`, {
                    accountId: currentAccountId,
                    filters: {
                        selectedGenders: selectedDemographics.selectedGenders,
                        ageRange: selectedDemographics.selectedAgeRange === 'all' ? null : selectedDemographics.selectedAgeRange,
                        membershipStatus: selectedDemographics.membershipStatus === 'all' ? null : selectedDemographics.membershipStatus
                    }
                });

                if (result && result.success) {
                    setShowConfetti(true);
                    setTimeout(() => setShowConfetti(false), 5000);

                    setDeliveryStats({
                        totalAttempted: result.totalAttempted || 0,
                        successfulSends: result.successfulSends || 0,
                        failedCount: result.failedCount || 0
                    });

                    setDeliveryStatsOpen(true);
                    await loadBlasts(currentAccountId);
                    setBlastToSend(null);
                    setSendConfirmOpen(false);
                }
            } catch (error) {
                console.error('Error sending blast:', error);

                // Handle the error response from our API
                if (error.status === 400 && error.error) {
                    setErrorMessage(error.error);
                } else if (error.message) {
                    setErrorMessage(error.message);
                } else {
                    setErrorMessage('Failed to send blast');
                }

                setErrorSnackbarOpen(true);
            }
        }
    };

    const handleEditClick = (blast) => {
        setEditingId(blast.id);
        setEditMessage(blast.message);
    };

    const handleEditChange = (e) => {
        setEditMessage(e.target.value);
    };

    const handleEditBlur = async (id) => {
        await handleEditSubmit(id);
    };

    const handleEditKeyPress = (e, id) => {
        if (e.key === 'Enter') {
            handleEditSubmit(id);
        }
    };

    const isMobile = window.innerWidth <= 600;

    const getFilteredCount = async (targetingOptions) => {
        if (!targetingOptions) return 'All Users';

        try {
            const response = await api.post('/api/users/count', {
                targetingOptions: {
                    selectedGenders: targetingOptions.selectedGenders,
                    selectedAgeRange: targetingOptions.selectedAgeRange,
                    membershipStatus: targetingOptions.membershipStatus
                }
            });
            return response.count || 0;
        } catch (error) {
            console.error('Error fetching filtered count:', error);
            return 'Error';
        }
    };

    // Add this function to fetch recipient counts
    const fetchRecipientCount = async (blast) => {
        if (!blast.targeting) return;

        try {
            // Match the same filter structure as used in send blast
            const filters = {
                selectedGenders: blast.targeting.genders,
                ageRange: blast.targeting.ageRange !== 'all' ? blast.targeting.ageRange : null,
                membershipStatus: blast.targeting.membershipStatus !== 'all' ? blast.targeting.membershipStatus : null
            };

            const response = await api.post(`/api/blasts/test-count`, {
                accountId: currentAccountId,
                filters: filters
            });

            if (response.success) {
                setRecipientCounts(prev => ({
                    ...prev,
                    [blast.id]: response.matchedUsers
                }));
            }
        } catch (error) {
            console.error('Error fetching recipient count:', error);
        }
    };

    // Add this effect to fetch counts for scheduled blasts
    useEffect(() => {
        const scheduledBlasts = blasts.filter(blast => blast.status === "scheduled");
        scheduledBlasts.forEach(blast => {
            if (blast.targeting) {
                fetchRecipientCount(blast);
            }
        });
    }, [blasts, currentAccountId]);

    // Update the recipient count display
    const getDisplayRecipientCount = (blast) => {
        if (blast.recipientCount !== undefined) {
            return `${blast.recipientCount}`;
        }

        const count = recipientCounts[blast.id];
        if (count !== undefined) {
            return `${count}`;
        }

        if (!blast.targeting) {
            return `${userCount}`;
        }

        return 'Calculating...';
    };

    // Completely rewrite the RecommendationsList component to fix DOM nesting issues
    const RecommendationsList = ({ recommendations, onSelectRecommendation }) => {
        return (
            <Box
                sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                    bgcolor: '#f8f8f8',
                    border: '1px solid #e0e0e0',
                    boxShadow: 3
                }}
            >
                <Box
                    sx={{
                        fontWeight: 'bold',
                        color: '#4a148c',
                        borderBottom: '2px solid #4a148c',
                        pb: 1,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <Send sx={{ mr: 1 }} />
                    <Typography variant="h6">
                        Message Recommendations
                    </Typography>
                </Box>

                {recommendations.map((recommendation, index) => (
                    <Box
                        key={index}
                        sx={{
                            mb: 2,
                            cursor: 'pointer',
                            borderRadius: 2,
                            border: '1px solid #e0e0e0',
                            p: 2,
                            bgcolor: 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 4px 20px rgba(74, 20, 140, 0.15)',
                                borderColor: '#4a148c',
                            }
                        }}
                        onClick={() => onSelectRecommendation(recommendation)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box
                                sx={{
                                    minWidth: '30px',
                                    height: '30px',
                                    width: '30px',
                                    borderRadius: '50%',
                                    bgcolor: '#4a148c',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    mr: 2
                                }}
                            >
                                {index + 1}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                    {recommendation}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {recommendation.length} characters
                                    </Typography>
                                    <Box
                                        sx={{
                                            ml: 1,
                                            fontSize: '0.7rem',
                                            bgcolor: 'rgba(74, 20, 140, 0.1)',
                                            color: '#4a148c',
                                            border: '1px solid #4a148c',
                                            borderRadius: '16px',
                                            px: 1,
                                            py: 0.5
                                        }}
                                    >
                                        Click to use
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    };

    const [ideasDialogOpen, setIdeasDialogOpen] = useState(false);

    const fetchRecommendations = async () => {
        console.log('Starting fetchRecommendations with context:', contextInput);
        setLoadingIdeas(true);
        setRecommendations([]);

        try {
            console.log("Making API request to /api/ai/recommendations");
            const response = await api.post('/api/ai/recommendations', {
                context: contextInput
            });

            console.log("API Response received:", response);
            
            // The response is already the parsed JSON data
            if (response && response.recommendations) {
                console.log("Setting recommendations:", response.recommendations);
                setRecommendations(response.recommendations);
            } else {
                console.warn("Unexpected format for recommendations:", response);
                setRecommendations([]);
            }
        } catch (error) {
            console.error('Error getting message ideas:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                status: error.status
            });
            setRecommendations([]);
        } finally {
            console.log('Loading state set to false');
            setLoadingIdeas(false);
        }
    };

    // Add useEffect to monitor recommendations state changes
    useEffect(() => {
        console.log('Recommendations state changed:', recommendations);
    }, [recommendations]);

    // Add useEffect to monitor loading state changes
    useEffect(() => {
        console.log('LoadingIdeas state changed:', loadingIdeas);
    }, [loadingIdeas]);

    // Add useEffect to monitor context input changes
    useEffect(() => {
        console.log('Context input changed:', contextInput);
    }, [contextInput]);

    // Add useEffect to monitor dialog state changes
    useEffect(() => {
        console.log('Ideas dialog state changed:', ideasDialogOpen);
    }, [ideasDialogOpen]);

    return (
        <div className="max-w-7xl mx-auto px-8 py-6">
            <div className="bg-gray-100 p-4 rounded-2xl shadow-xl overflow-hidden">
                {/* Draft Blasts Section */}
                <Box className="p-4 mb-4 bg-white rounded-lg">
                    <Typography variant="h5" className="font-bold" style={{ fontWeight: 700 }}>
                        Draft Blasts
                    </Typography>
                    {blasts.filter(blast => !blast.status).length === 0 ? (
                        <Typography color="textSecondary" className="mt-4">
                            You have not drafted any blasts yet. Add a blast at the bottom of the page to get started!
                        </Typography>
                    ) : (
                        <div className={isMobile ? "blast-table-mobile" : "blast-table-desktop"}>
                            {isMobile ? (
                                blasts.filter(blast => !blast.status).map((blast) => (
                                    <div key={blast.id} className="blast-row">
                                        <div className="blast-message" onClick={() => handleEditClick(blast)}>
                                            {editingId === blast.id ? (
                                                <TextField
                                                    value={editMessage}
                                                    onChange={handleEditChange}
                                                    onBlur={() => handleEditBlur(blast.id)}
                                                    onKeyPress={(e) => handleEditKeyPress(e, blast.id)}
                                                    autoFocus
                                                />
                                            ) : (
                                                blast.message
                                            )}
                                        </div>
                                        <div className="blast-actions">
                                            <Tooltip title="Send Blast" arrow placement="top">
                                                <IconButton onClick={() => handleSendClick(blast)}>
                                                    <Send className="w-5 h-5" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Delete Blast" arrow placement="top">
                                                <IconButton onClick={() => handleDeleteClick(blast)}>
                                                    <Trash2 className="w-5 h-5" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Schedule Blast" arrow placement="top">
                                                <IconButton onClick={() => handleScheduleClick(blast)}>
                                                    <Calendar className="w-5 h-5" />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Table className="w-full">
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="w-[40%] font-bold text-lg">Blast</TableHead>
                                            <TableHead className="w-[15%] font-bold text-lg text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {blasts.filter(blast => !blast.status).map((blast) => (
                                            <TableRow key={blast.id}>
                                                <TableCell onClick={() => handleEditClick(blast)} className="font-medium cursor-pointer">
                                                    {editingId === blast.id ? (
                                                        <TextField
                                                            value={editMessage}
                                                            onChange={handleEditChange}
                                                            onBlur={() => handleEditBlur(blast.id)}
                                                            onKeyPress={(e) => handleEditKeyPress(e, blast.id)}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        blast.message
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <div className="flex justify-end gap-2">
                                                        <Tooltip title="Send Blast" arrow placement="top">
                                                            <IconButton onClick={() => handleSendClick(blast)}>
                                                                <Send className="w-5 h-5" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Delete Blast" arrow placement="top">
                                                            <IconButton onClick={() => handleDeleteClick(blast)}>
                                                                <Trash2 className="w-5 h-5" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Schedule Blast" arrow placement="top">
                                                            <IconButton onClick={() => handleScheduleClick(blast)}>
                                                                <Calendar className="w-5 h-5" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    )}
                </Box>

                {/* Scheduled Blasts Section */}
                <Box className="p-4 mb-4 bg-white rounded-lg">
                    <Typography variant="h5" className="font-bold" style={{ fontWeight: 700 }}>
                        Scheduled Blasts
                    </Typography>
                    {blasts.filter(blast => blast.status === "scheduled").length === 0 ? (
                        <Typography color="textSecondary" className="mt-4">
                            You have not yet scheduled any blasts. Schedule ahead of time to plan ahead!
                        </Typography>
                    ) : (
                        <div className={isMobile ? "blast-table-mobile" : "blast-table-desktop"}>
                            {isMobile ? (
                                blasts.filter(blast => blast.status === "scheduled").map((blast) => (
                                    <div key={blast.id} className="blast-row">
                                        <div className="blast-message" onClick={() => handleEditClick(blast)}>
                                            {editingId === blast.id ? (
                                                <TextField
                                                    value={editMessage}
                                                    onChange={handleEditChange}
                                                    onBlur={() => handleEditBlur(blast.id)}
                                                    onKeyPress={(e) => handleEditKeyPress(e, blast.id)}
                                                    autoFocus
                                                />
                                            ) : (
                                                blast.message
                                            )}
                                        </div>
                                        <div className="blast-scheduled-time">
                                            <ScheduledDateDisplay
                                                scheduledDate={blast.scheduledDate}
                                            />
                                        </div>
                                        <div className="blast-recipients">
                                            {console.log('Rendering blast targeting options:', blast.targetingOptions)}
                                            {blast.targetingOptions ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {/* Gender Tags */}
                                                    {!blast.targetingOptions.selectedGenders.includes('all') && (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                            {blast.targetingOptions.selectedGenders.map((gender) => (
                                                                <Chip
                                                                    key={gender}
                                                                    label={gender === 'nonBinary' ? 'Non-binary' :
                                                                        gender.charAt(0).toUpperCase() + gender.slice(1)}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            gender === 'male' ? '#E3F2FD' :
                                                                                gender === 'female' ? '#FCE4EC' :
                                                                                    gender === 'nonBinary' ? '#F3E5F5' :
                                                                                        '#EDE7F6',
                                                                        color:
                                                                            gender === 'male' ? '#1565C0' :
                                                                                gender === 'female' ? '#C2185B' :
                                                                                    gender === 'nonBinary' ? '#7B1FA2' :
                                                                                        '#4527A0',
                                                                        fontWeight: 500,
                                                                        '&:hover': { opacity: 0.9 }
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}

                                                    {/* Age Range Tag */}
                                                    {blast.targetingOptions.selectedAgeRange !== 'all' && (
                                                        <Chip
                                                            label={`Ages ${blast.targetingOptions.selectedAgeRange}`}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: '#E8F5E9',
                                                                color: '#2E7D32',
                                                                fontWeight: 500,
                                                                '&:hover': { opacity: 0.9 }
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    Everyone
                                                </Typography>
                                            )}
                                        </div>
                                        <div className="blast-actions">
                                            <IconButton onClick={() => handleDeleteClick(blast)}>
                                                <Trash2 className="w-5 h-5" />
                                            </IconButton>
                                            <IconButton onClick={() => handleScheduleClick(blast)}>
                                                <Calendar className="w-5 h-5" />
                                            </IconButton>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Table className="w-full">
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="w-[40%] font-bold text-lg">Blast</TableHead>
                                            <TableHead className="w-[15%] font-bold text-lg text-center">Scheduled Date</TableHead>
                                            <TableHead className="w-[15%] font-bold text-lg text-center">Demographics</TableHead>
                                            <TableHead className="w-[15%] font-bold text-lg text-center">Number of Recipients</TableHead>
                                            <TableHead className="w-[15%] font-bold text-lg text-center">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {blasts.filter(blast => blast.status === "scheduled").map((blast) => (
                                            <TableRow key={blast.id}>
                                                <TableCell onClick={() => handleEditClick(blast)} className="font-medium cursor-pointer">
                                                    {editingId === blast.id ? (
                                                        <TextField
                                                            value={editMessage}
                                                            onChange={handleEditChange}
                                                            onBlur={() => handleEditBlur(blast.id)}
                                                            onKeyPress={(e) => handleEditKeyPress(e, blast.id)}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        blast.message
                                                    )}
                                                </TableCell>
                                                <TableCell align="center" className="w-[15%]">
                                                    <ScheduledDateDisplay
                                                        scheduledDate={blast.scheduledDate}
                                                        timeSlot={blast.timeSlot}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" className="w-[15%]">
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                                        {blast.targeting ? (
                                                            <>
                                                                {/* Gender Tags */}
                                                                {blast.targeting.genders  && (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                                                        {blast.targeting.genders.map((gender) => (
                                                                            <Chip
                                                                                key={gender}
                                                                                label={ gender === 'all' ? 'All Genders' : gender === 'nonBinary' ? 'Non-binary' :
                                                                                    gender.charAt(0).toUpperCase() + gender.slice(1)}
                                                                                size="small"
                                                                                sx={{
                                                                                    backgroundColor:
                                                                                        gender === 'male' ? '#E3F2FD' :
                                                                                            gender === 'female' ? '#FCE4EC' :
                                                                                                gender === 'nonBinary' ? '#F3E5F5' :
                                                                                                    '#EDE7F6',
                                                                                    color:
                                                                                        gender === 'male' ? '#1565C0' :
                                                                                            gender === 'female' ? '#C2185B' :
                                                                                                gender === 'nonBinary' ? '#7B1FA2' :
                                                                                                    '#4527A0',
                                                                                    fontWeight: 500,
                                                                                    '&:hover': { opacity: 0.9 }
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                                {/* Age Range Tag */}
                                                                {blast.targeting.ageRange && (
                                                                    <Chip
                                                                        label={blast.targeting.ageRange === 'all' ? 'All Ages' : `Ages ${blast.targeting.ageRange}`}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: '#E8F5E9',
                                                                            color: '#2E7D32',
                                                                            fontWeight: 500,
                                                                            '&:hover': { opacity: 0.9 }
                                                                        }}
                                                                    />
                                                                )}
                                                                {/* Membership Status Tag */}
                                                                {blast.targeting.membershipStatus && blast.targeting.membershipStatus !== 'all' && (
                                                                    <Chip
                                                                        label={blast.targeting.membershipStatus === 'yes' ? 'Members Only' : 'Non-Members Only'}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: '#FFF3E0',
                                                                            color: '#E65100',
                                                                            fontWeight: 500,
                                                                            '&:hover': { opacity: 0.9 }
                                                                        }}
                                                                    />
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Everyone
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" className="w-[15%]">
                                                    <Typography variant="body2">
                                                        {getDisplayRecipientCount(blast)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" className="w-[15%]">
                                                    <div className="flex justify-center gap-2">
                                                        <Tooltip title="Delete Blast" arrow placement="top">
                                                            <IconButton onClick={() => handleDeleteClick(blast)}>
                                                                <Trash2 className="w-5 h-5" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Reschedule Blast" arrow placement="top">
                                                            <IconButton onClick={() => handleScheduleClick(blast)}>
                                                                <Calendar className="w-5 h-5" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    )}
                </Box>

            </div>

            {/* Other Dialogs and Snackbar */}
            <DeleteBlastDialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDeleteConfirm}
            />

            <SendBlastDialog
                open={sendConfirmOpen}
                onClose={() => setSendConfirmOpen(false)}
                onConfirm={handleSendConfirm}
                blast={blastToSend}
                userCount={userCount}
            />

            <ScheduleBlastDialog
                open={scheduleDialogOpen}
                onClose={() => setScheduleDialogOpen(false)}
                onConfirm={handleScheduleConfirm}
                blast={blastToSchedule}
            />

            <DeliveryStatsDialog
                open={deliveryStatsOpen}
                onClose={() => setDeliveryStatsOpen(false)}
                stats={deliveryStats}
            />

            <TargetingDemographicsDialog
                open={targetingDialogOpen}
                onClose={() => setTargetingDialogOpen(false)}
                onConfirm={handleTargetingConfirm}
                includeMembershipQuestion={userData?.includeMembershipQuestion || false}
            />

            <Snackbar
                open={errorSnackbarOpen}
                autoHideDuration={6000}
                onClose={() => setErrorSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setErrorSnackbarOpen(false)}
                    severity="error"
                    sx={{ width: '100%' }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>

            {showConfetti && <Confetti />}

            {/* Add Blast Section */}
            <div className="bg-white p-2 rounded-2xl mt-8 mb-4 mx-4 border-2 border-[#2B0063] shadow-xl">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <Input
                            placeholder="Type your blast message..."
                            style={{ backgroundColor: '#ffffff' }}
                            className="w-full px-4 py-3 rounded-xl
                                     focus:outline-none focus:ring-2 focus:ring-purple-900/20 
                                     border-0
                                     transition-all duration-200"
                            value={addedBlast}
                            onChange={(e) => setAddedBlast(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAdd(e);
                                }
                            }}
                        />
                    </div>

                    <Button 
                        onClick={handleAdd} 
                        style={{ backgroundColor: '#2B0063' }}
                        className="text-white rounded-xl hover:bg-[#3d0087] transition-all duration-200
                                 px-6 py-2.5 font-semibold flex items-center justify-center
                                 [&>*]:text-white"
                    >
                        <PlusIcon className="w-4 h-4 mr-1.5" />
                        <span className="text-white">ADD</span>
                    </Button>
                </div>
            </div>

            {/* Update the button to show loading state */}
            <Tooltip title="Get AI-powered message suggestions" arrow placement="top">
                <Button
                    onClick={() => {
                        setIdeasDialogOpen(true);
                        if (contextInput.trim()) {
                            fetchRecommendations();
                        }
                    }}
                    variant="contained"
                    startIcon={<Sparkles className="w-4 h-4" />}
                    sx={{
                        mt: 2,
                        mb: 3,
                        bgcolor: '#4a148c',
                        color: 'white',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        textTransform: 'none',
                        boxShadow: '0 4px 10px rgba(74, 20, 140, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            bgcolor: '#6a1b9a',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 15px rgba(74, 20, 140, 0.4)',
                        },
                    }}
                >
                    Get Message Ideas
                </Button>
            </Tooltip>

            <Dialog 
                open={ideasDialogOpen} 
                onClose={() => {
                    console.log('Closing ideas dialog');
                    setIdeasDialogOpen(false);
                }} 
                maxWidth="md" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 8px 32px rgba(74, 20, 140, 0.2)',
                        background: 'linear-gradient(145deg, #ffffff, #f8f8f8)',
                        animation: 'dialogFadeIn 0.3s ease-out',
                        '@keyframes dialogFadeIn': {
                            '0%': {
                                opacity: 0,
                                transform: 'scale(0.95) translateY(10px)'
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'scale(1) translateY(0)'
                            }
                        }
                    }
                }}
            >
                <DialogTitle sx={{ 
                    bgcolor: '#4a148c', 
                    color: 'white',
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Sparkles className="w-5 h-5" />
                    Message Ideas
                </DialogTitle>
                <DialogContent sx={{ 
                    p: 3,
                    '&.MuiDialogContent-root': {
                        pt: 3
                    }
                }}>
                    <TextField
                        label="What's your message about?"
                        placeholder="e.g., happy hour, weekend special, live music"
                        fullWidth
                        margin="normal"
                        value={contextInput}
                        onChange={(e) => {
                            console.log('Context input changed to:', e.target.value);
                            setContextInput(e.target.value);
                        }}
                        variant="outlined"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !loadingIdeas) {
                                console.log('Enter key pressed, fetching recommendations');
                                fetchRecommendations();
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#4a148c',
                                    }
                                },
                                '&.Mui-focused': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#4a148c',
                                        borderWidth: 2
                                    }
                                }
                            }
                        }}
                    />

                    {loadingIdeas ? (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center',
                            my: 4,
                            gap: 2
                        }}>
                            <Box sx={{
                                position: 'relative',
                                width: 80,
                                height: 80,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {/* Outer ring */}
                                <CircularProgress 
                                    variant="determinate"
                                    value={100}
                                    thickness={4}
                                    sx={{ 
                                        color: 'rgba(74, 20, 140, 0.1)',
                                        position: 'absolute'
                                    }}
                                    size={80}
                                />
                                {/* Animated ring */}
                                <CircularProgress 
                                    variant="indeterminate"
                                    thickness={4}
                                    sx={{ 
                                        color: '#4a148c',
                                        position: 'absolute',
                                        animation: 'spin 1.5s linear infinite, pulse 2s ease-in-out infinite',
                                        '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' }
                                        },
                                        '@keyframes pulse': {
                                            '0%': { opacity: 0.6 },
                                            '50%': { opacity: 1 },
                                            '100%': { opacity: 0.6 }
                                        }
                                    }}
                                    size={80}
                                />
                                {/* Center icon */}
                                <Sparkles className="w-8 h-8" style={{ 
                                    color: '#4a148c',
                                    animation: 'float 2s ease-in-out infinite',
                                    '@keyframes float': {
                                        '0%': { transform: 'translateY(0)' },
                                        '50%': { transform: 'translateY(-5px)' },
                                        '100%': { transform: 'translateY(0)' }
                                    }
                                }} />
                            </Box>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    color: '#4a148c',
                                    fontWeight: 'medium',
                                    animation: 'fadeInOut 2s ease-in-out infinite',
                                    '@keyframes fadeInOut': {
                                        '0%': { opacity: 0.6 },
                                        '50%': { opacity: 1 },
                                        '100%': { opacity: 0.6 }
                                    }
                                }}
                            >
                                Generating ideas...
                            </Typography>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{
                                    animation: 'slideUp 0.5s ease-out',
                                    '@keyframes slideUp': {
                                        '0%': { 
                                            opacity: 0,
                                            transform: 'translateY(10px)'
                                        },
                                        '100%': { 
                                            opacity: 1,
                                            transform: 'translateY(0)'
                                        }
                                    }
                                }}
                            >
                                Our AI is crafting the perfect messages for you
                            </Typography>
                        </Box>
                    ) : recommendations.length > 0 ? (
                        <List sx={{ 
                            mt: 2,
                            '& .MuiListItem-root': {
                                animation: 'slideIn 0.3s ease-out',
                                animationFillMode: 'forwards',
                                opacity: 0,
                                transform: 'translateY(10px)',
                                '@keyframes slideIn': {
                                    '0%': {
                                        opacity: 0,
                                        transform: 'translateY(10px)'
                                    },
                                    '100%': {
                                        opacity: 1,
                                        transform: 'translateY(0)'
                                    }
                                }
                            }
                        }}>
                            {recommendations.map((recommendation, index) => (
                                <ListItem
                                    key={index}
                                    button="true"
                                    onClick={() => {
                                        console.log('Recommendation selected:', recommendation);
                                        setAddedBlast(recommendation);
                                        setIdeasDialogOpen(false);
                                    }}
                                    sx={{
                                        mb: 2,
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        animationDelay: `${index * 0.1}s`,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 20px rgba(74, 20, 140, 0.15)',
                                            backgroundColor: 'rgba(74, 20, 140, 0.08)',
                                            '&::after': {
                                                transform: 'translateX(0)'
                                            }
                                        },
                                        '&:active': {
                                            transform: 'translateY(0)'
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            background: 'linear-gradient(90deg, transparent, rgba(74, 20, 140, 0.1), transparent)',
                                            transform: 'translateX(-100%)',
                                            transition: 'transform 0.6s ease',
                                        }
                                    }}
                                >
                                    <ListItemText 
                                        primary={recommendation}
                                        primaryTypographyProps={{
                                            sx: { 
                                                fontWeight: 'medium',
                                                color: '#4a148c',
                                                position: 'relative',
                                                zIndex: 1
                                            }
                                        }}
                                        secondary={
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                mt: 1,
                                                gap: 1,
                                                position: 'relative',
                                                zIndex: 1
                                            }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {recommendation.length} characters
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        bgcolor: 'rgba(74, 20, 140, 0.1)',
                                                        color: '#4a148c',
                                                        border: '1px solid #4a148c',
                                                        borderRadius: '16px',
                                                        px: 1,
                                                        py: 0.5,
                                                        transition: 'all 0.3s ease',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(74, 20, 140, 0.2)',
                                                            transform: 'scale(1.05)',
                                                            boxShadow: '0 2px 8px rgba(74, 20, 140, 0.2)'
                                                        },
                                                        '&:active': {
                                                            transform: 'scale(0.95)',
                                                            bgcolor: 'rgba(74, 20, 140, 0.3)'
                                                        }
                                                    }}
                                                >
                                                    Click to use
                                                </Box>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            my: 4,
                            gap: 2,
                            animation: 'fadeIn 0.5s ease-out'
                        }}>
                            <Sparkles className="w-12 h-12" style={{ 
                                color: '#4a148c',
                                opacity: 0.5,
                                animation: 'float 3s ease-in-out infinite'
                            }} />
                            <Typography 
                                variant="body2" 
                                color="textSecondary" 
                                sx={{ 
                                    textAlign: 'center',
                                    animation: 'slideUp 0.5s ease-out'
                                }}
                            >
                                No recommendations available. Try a different topic.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ 
                    p: 2,
                    borderTop: '1px solid rgba(74, 20, 140, 0.1)'
                }}>
                    <Button 
                        onClick={() => {
                            console.log('Cancel button clicked');
                            setIdeasDialogOpen(false);
                        }}
                        sx={{
                            color: '#666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            console.log('Get Ideas button clicked');
                            fetchRecommendations();
                        }}
                        color="primary"
                        variant="contained"
                        disabled={loadingIdeas}
                        sx={{
                            bgcolor: '#4a148c',
                            '&:hover': {
                                bgcolor: '#6a1b9a',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(74, 20, 140, 0.3)'
                            },
                            '&:active': {
                                transform: 'translateY(0)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {loadingIdeas ? "Generating..." : "Get Ideas"}
                    </Button>
                </DialogActions>
            </Dialog>


        </div>
    );
}